import { useEffect, useState } from "react";
import { bulkUpdateProspectOwner, deleteProspect, fetchProspects, updateProspectOwner, type ProspectListItem } from "@/lib/prospectsApi";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Search, ArrowUpDown, MoreHorizontal, Trash2 } from "lucide-react";
import ProspectDetailDialog from "@/components/prospects/ProspectDetailDialog";
import { getStageBadgeVariant, getStageLabel } from "@/components/prospects/prospectConstants";
import { useAuth } from "@/contexts/AuthContext";
import { lookupUsersByRoles } from "@/lib/usersApi";

interface ProspectListProps {
  refreshKey?: number;
}

type SortField = "company_name" | "stage" | "next_follow_up_at" | null;

type SortOrder = "asc" | "desc";

const ProspectList = ({ refreshKey }: ProspectListProps) => {
  const { toast } = useToast();
  const { userRole } = useAuth();
  const [prospects, setProspects] = useState<ProspectListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const [selectedProspectId, setSelectedProspectId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState<Array<{ id: string; full_name: string; email?: string | null }>>([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [prospectToDelete, setProspectToDelete] = useState<ProspectListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkOwnerId, setBulkOwnerId] = useState<string>("");
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 300);
  const showOwnerColumn = userRole === "ceo" || userRole === "general_manager";
  const canBulk = showOwnerColumn;

  useEffect(() => {
    loadProspects();
  }, [currentPage, pageSize, sortField, sortOrder, debouncedSearch, refreshKey]);

  useEffect(() => {
    if (!showOwnerColumn) return;
    const loadOwners = async () => {
      try {
        setOwnersLoading(true);
        const users = await lookupUsersByRoles(["sales_staff", "general_manager", "ceo"]);
        const normalized = (users || []).map((user) => ({
          id: user.id,
          full_name: user.full_name,
          email: user.email,
        }));
        normalized.sort((a, b) => a.full_name.localeCompare(b.full_name));
        setOwnerOptions(normalized);
      } catch (error) {
        console.error("Error loading owners:", error);
      } finally {
        setOwnersLoading(false);
      }
    };
    loadOwners();
  }, [showOwnerColumn]);

  useEffect(() => {
    if (selectedIds.size === 0) {
      setBulkDialogOpen(false);
      setBulkOwnerId("");
    }
  }, [selectedIds]);

  const loadProspects = async () => {
    try {
      setIsLoading(true);
      const response = await fetchProspects({
        page: currentPage,
        size: pageSize,
        search: debouncedSearch,
        sortField: sortField ?? undefined,
        sortOrder,
      });
      const items = Array.isArray(response?.items) ? response.items : [];
      const total = typeof response?.total === "number" ? response.total : items.length;
      setProspects(items);
      setTotalCount(total);
      setSelectedIds(new Set());
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOwnerChange = async (prospectId: number, ownerId: string) => {
    try {
      const normalized = ownerId === "unassigned" ? null : ownerId;
      const updated = await updateProspectOwner(prospectId, normalized);
      setProspects((prev) =>
        prev.map((item) =>
          item.id === prospectId
            ? {
                ...item,
                owner_id: updated.owner_id ?? null,
                owner_name: updated.owner_name ?? null,
                owner_email: updated.owner_email ?? null,
              }
            : item,
        ),
      );
      toast({ title: "Owner updated", description: "Responsible updated successfully." });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleRowClick = async (item: ProspectListItem) => {
    try {
      setSelectedProspectId(item.id);
      setDetailOpen(true);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortField(null);
        setSortOrder("desc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const canDelete = userRole === "ceo";

  const handleDeleteClick = (prospect: ProspectListItem) => {
    setProspectToDelete(prospect);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!prospectToDelete) return;
    try {
      setDeleting(true);
      await deleteProspect(prospectToDelete.id);
      toast({
        title: "Prospect deleted",
        description: `Prospect "${prospectToDelete.company_name}" has been deleted successfully.`,
      });
      setDeleteDialogOpen(false);
      setProspectToDelete(null);
      loadProspects();
    } catch (error: any) {
      toast({ title: "Error deleting prospect", description: error.message, variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkOwnerUpdate = async () => {
    if (!bulkOwnerId || selectedIds.size === 0) return;
    try {
      setBulkUpdating(true);
      const normalized = bulkOwnerId === "unassigned" ? null : bulkOwnerId;
      await bulkUpdateProspectOwner(Array.from(selectedIds), normalized);
      toast({ title: "Owners updated", description: "Responsible updated for selected prospects." });
      setBulkOwnerId("");
      setSelectedIds(new Set());
      loadProspects();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setBulkUpdating(false);
    }
  };

  const toggleSelectAll = (checked: boolean | "indeterminate") => {
    if (!canBulk) return;
    const ids = prospects.map((item) => item.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked === true) {
        ids.forEach((id) => next.add(id));
      } else {
        ids.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const toggleSelect = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const columnCount = canBulk ? 8 : 5;

  const getBusinessTypeLabel = (value?: string | null) => {
    if (value === "operator") return "Operator";
    if (value === "agency") return "Agency";
    if (value === "final_client") return "Final Client";
    return "-";
  };

  return (
    <div className="space-y-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by company, contact, or email..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            {canBulk && (
              <TableRow className="bg-muted/30">
                <TableHead colSpan={columnCount} className="py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Selected ({selectedIds.size})</span>
                    {selectedIds.size > 0 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setBulkDialogOpen(true)}>
                            Change responsible
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </TableHead>
              </TableRow>
            )}
            <TableRow>
              {canBulk && (
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={
                      prospects.length > 0
                        ? prospects.every((item) => selectedIds.has(item.id))
                          ? true
                          : prospects.some((item) => selectedIds.has(item.id))
                            ? "indeterminate"
                            : false
                        : false
                    }
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all prospects"
                  />
                </TableHead>
              )}
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => handleSort("company_name")}
                >
                  Company
                  <ArrowUpDown className={`ml-2 h-4 w-4 ${sortField === "company_name" ? "text-primary" : ""}`} />
                </Button>
              </TableHead>
              <TableHead>Business Type</TableHead>
              {showOwnerColumn && <TableHead>Responsible</TableHead>}
              <TableHead>Primary Contact</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => handleSort("stage")}
                >
                  Stage
                  <ArrowUpDown className={`ml-2 h-4 w-4 ${sortField === "stage" ? "text-primary" : ""}`} />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => handleSort("next_follow_up_at")}
                >
                  Next Follow-up
                  <ArrowUpDown className={`ml-2 h-4 w-4 ${sortField === "next_follow_up_at" ? "text-primary" : ""}`} />
                </Button>
              </TableHead>
              {showOwnerColumn && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="text-center py-6">
                  Loading prospects...
                </TableCell>
              </TableRow>
            ) : prospects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="text-center py-6">
                  No prospects found
                </TableCell>
              </TableRow>
            ) : (
              prospects.map((prospect) => (
                <TableRow
                  key={prospect.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(prospect)}
                >
                  {canBulk && (
                    <TableCell
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelect(prospect.id, !selectedIds.has(prospect.id));
                      }}
                      className="cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedIds.has(prospect.id)}
                        onCheckedChange={(checked) => toggleSelect(prospect.id, checked === true)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${prospect.company_name}`}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium">{prospect.company_name}</p>
                      <p className="text-xs text-muted-foreground">{prospect.country || "-"}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{getBusinessTypeLabel(prospect.business_type)}</Badge>
                  </TableCell>
                  {showOwnerColumn && (
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm">{prospect.owner_name || "-"}</p>
                        <p className="text-xs text-muted-foreground">{prospect.owner_email || ""}</p>
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="space-y-1">
                      <p className="text-sm">{prospect.primary_contact_name || "-"}</p>
                      <p className="text-xs text-muted-foreground">{prospect.primary_contact_email || ""}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStageBadgeVariant(prospect.stage)}>{getStageLabel(prospect.stage)}</Badge>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-muted-foreground">
                      {prospect.next_follow_up_at ? new Date(prospect.next_follow_up_at).toLocaleString() : "-"}
                    </p>
                  </TableCell>
                  {showOwnerColumn && (
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2 items-center">
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(prospect);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Select
                          value={prospect.owner_id || "unassigned"}
                          onValueChange={(value) => handleOwnerChange(prospect.id, value)}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Change responsible" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {ownersLoading && (
                              <SelectItem value="loading" disabled>
                                Loading...
                              </SelectItem>
                            )}
                            {ownerOptions.map((owner) => (
                              <SelectItem key={owner.id} value={owner.id}>
                                {owner.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="text-sm text-muted-foreground">{totalCount} total prospects</div>
      </div>

      <ProspectDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        prospectId={selectedProspectId}
        onUpdated={loadProspects}
      />

      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change responsible</DialogTitle>
            <DialogDescription>
              Update the responsible for {selectedIds.size} selected prospects.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Responsible</label>
            <Select value={bulkOwnerId} onValueChange={setBulkOwnerId}>
              <SelectTrigger>
                <SelectValue placeholder="Select owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {ownersLoading && (
                  <SelectItem value="loading" disabled>
                    Loading...
                  </SelectItem>
                )}
                {ownerOptions.map((owner) => (
                  <SelectItem key={owner.id} value={owner.id}>
                    {owner.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkOwnerUpdate}
              disabled={!bulkOwnerId || selectedIds.size === 0 || bulkUpdating}
            >
              {bulkUpdating ? "Updating..." : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prospect</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{prospectToDelete?.company_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete Prospect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProspectList;
